/**
 * Min/max peak reduction for the waveform canvas.
 *
 * One column per device pixel: for every column we keep the lowest and highest
 * sample it covers, which is what makes a hand-rolled waveform look like a
 * waveform instead of a noisy line — averaging or subsampling would drop the
 * transients that tell a human where a word actually starts.
 */
export interface Peaks {
  /** Lowest sample per column, in [-1, 0]. */
  min: Float32Array;
  /** Highest sample per column, in [0, 1]. */
  max: Float32Array;
  /** Number of columns; `min.length === max.length === columns`. */
  columns: number;
}

/** Reduce `samples` to `columns` min/max pairs. Empty input yields flat zeros. */
export function computePeaks(samples: Float32Array, columns: number): Peaks {
  const cols = Math.max(1, Math.floor(columns));
  const min = new Float32Array(cols);
  const max = new Float32Array(cols);
  if (samples.length === 0) return { min, max, columns: cols };

  const per = samples.length / cols;
  for (let c = 0; c < cols; c++) {
    const from = Math.floor(c * per);
    // Always consume at least one sample, even when there are fewer samples
    // than columns (a very short window zoomed across a wide canvas).
    const to = Math.max(from + 1, Math.min(samples.length, Math.floor((c + 1) * per)));
    let lo = 0;
    let hi = 0;
    for (let i = from; i < to; i++) {
      const v = samples[i];
      if (!Number.isFinite(v)) continue;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    min[c] = lo;
    max[c] = hi;
  }
  return { min, max, columns: cols };
}

/**
 * Loudest absolute peak, used to scale the drawing so a quiet recording still
 * fills the canvas. Returns 0 for silence.
 */
export function peakAmplitude(peaks: Peaks): number {
  let amp = 0;
  for (let i = 0; i < peaks.columns; i++) {
    const a = Math.max(Math.abs(peaks.min[i]), Math.abs(peaks.max[i]));
    if (a > amp) amp = a;
  }
  return amp;
}

/**
 * A tick spacing that yields roughly 4-8 labelled ticks across `spanSec`.
 * The ladder stops at 0.05 s: the trimmer window is ~1 s of context either side
 * of a word, so sub-100 ms ticks are what make a boundary readable.
 */
export function tickStepFor(spanSec: number): number {
  const ladder = [0.05, 0.1, 0.25, 0.5, 1, 2, 5, 10, 30, 60];
  for (const step of ladder) {
    if (spanSec / step <= 8) return step;
  }
  return ladder[ladder.length - 1];
}
